import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sb } from '../supabase';
import { getSupplierOnboardingState, getSupplierPrimaryRoute } from '../lib/supplierOnboarding';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';
import { useHioBuyOnly } from '../lib/marketMode';
import { buyerNav, supplierNav, navLabel } from '../lib/navItems';
import { cartCount, onCartChange } from '../lib/marketCart';

export default function Navbar({ user, profile, lang, setLang, setUser, setProfile, logoOnly }) {
  const nav = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);

  const isAr       = lang === 'ar';
  const isPreview  = localStorage.getItem('maabar_preview') === '1';
  const homeRoute  = isPreview ? '/dashboard' : '/';
  const isSupplier = profile?.role === 'supplier';
  // HioBuy-only mode: "Products" is the marketplace (lib/marketMode.js).
  const hioBuyOnly = useHioBuyOnly().hioBuyOnly;
  const productsPath = hioBuyOnly ? '/market' : '/products';
  const supplierState = isSupplier ? getSupplierOnboardingState(profile, user) : null;
  const supplierPrimaryRoute = isSupplier ? getSupplierPrimaryRoute(profile, user) : '/dashboard';
  const supplierDashboardLabel = !isSupplier
    ? (isAr ? 'لوحتي' : lang === 'zh' ? '控制台' : 'Dashboard')
    : supplierState?.canAccessOperationalFeatures
      ? (isAr ? 'لوحتي' : lang === 'zh' ? '控制台' : 'Dashboard')
      : supplierState?.isUnderReviewStage
        ? (isAr ? 'حالة الطلب' : lang === 'zh' ? '审核状态' : 'Application Status')
        : supplierState?.isInactiveStage
          ? (isAr ? 'الحساب' : lang === 'zh' ? '账户' : 'Account')
          : (isAr ? 'لوحة المورد' : lang === 'zh' ? '供应商控制台' : 'Supplier Dashboard');
  const currentRoute = `${location.pathname}${location.search}`;
  // '/dashboard' is the overview only — a `?tab=` link (طلبات التسعير…) must
  // not light it up too, or two items look selected at once.
  const isNavLinkActive = (path) => {
    if (path.includes('?')) return currentRoute === path;
    if (path === '/dashboard') {
      const tab = new URLSearchParams(location.search).get('tab');
      return location.pathname === path && (!tab || tab === 'overview');
    }
    return location.pathname === path;
  };

  const [cartLines, setCartLines] = useState(() => cartCount());
  useEffect(() => onCartChange((lines) => setCartLines(lines.length)), []);

  /* ─── Scroll ──────────────────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const doSignOut = async () => {
    await sb.auth.signOut();
    setUser(null);
    setProfile(null);
    nav('/');
  };

  /* ─── Door icons ──────────────────────────────────────────────────────────
     The house icon style, matched exactly: 24×24 viewBox, fill none, stroke
     currentColor (so both inherit the button's white), round caps and joins —
     the same parameters as the Icon set in components/factory/RequestQuoteModal
     and the inline icons in pages/Factories.

     `bag` is NEW: the codebase had no shopping-bag, store or cart glyph
     anywhere (the nearest, `box`, is the products/package icon and reads as
     cargo, not as buying).

     BOTH ARE SOLID MASSES, and that is what makes the pair balance. The first
     attempt reused the existing outline `factory` path at 1.7px, which went
     faint beside the bag: a 4-segment sawtooth outline has far less ink than a
     closed bag. Filling only the factory would just flip the imbalance, so the
     bag body is filled too. Both now: 18px, 2px stroke, one filled silhouette
     each, with the bag's handle left as an open arc because a filled handle
     reads as a blob at this size.

     Kept local rather than extracted into a shared module — two files owning a
     handful of inline icons is the existing pattern here, and a shared icon
     module is a bigger change than this one earns. */
  const DoorIcon = ({ name }) => {
    const p = {
      width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none',
      stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
      'aria-hidden': true, focusable: false,
    };
    return name === 'factory'
      ? (
        <svg {...p}>
          <path d="M16 20V5h4v15z" fill="currentColor" />
          <path d="M3 20v-8l5.5 3.2V12l5.5 3.2V20z" fill="currentColor" />
        </svg>
      )
      : (
        <svg {...p}>
          <path d="M6 8h12l-1 12H7L6 8z" fill="currentColor" />
          <path d="M9 8V6a3 3 0 0 1 6 0v2" />
        </svg>
      );
  };

  /* ─── Signed-out auth doors ───────────────────────────────────────────────
     TWO doors, equal weight, each saying who it is for.

     It used to be a solid "دخول / تسجيل" (which went to the BUYER signup) beside
     a faint "بوابة الموردين". Suppliers read the solid one as THE login and
     registered as buyers — a wrong account type, on the wrong side of the
     product, that nothing downstream corrects. The fix is not a bigger supplier
     link: it is refusing to imply that either audience is the default.

     So both buttons carry the same class in each context — `.nav-cta` on
     desktop, `.btn-dark-sm` in the mobile menu — rather than two classes that
     merely look similar today. Equal by construction, not by coincidence.

     ORDER IS BY LANGUAGE, not by importance. Arabic and English lead with the
     trader, because that is who reads those. Chinese leads with the supplier:
     the zh surface exists for the factories, and a Chinese factory should not
     have to read past a trader button to find its own door.

     Routes are UNCHANGED — /login/:role (App.js:401) already serves both. */
  const authDoors = (() => {
    const trader = {
      key: 'trader',
      icon: 'bag',
      path: '/login/buyer',
      label: isAr ? 'دخول التجار' : lang === 'zh' ? '买家登录' : 'Trader Login',
    };
    const supplier = {
      key: 'supplier',
      icon: 'factory',
      path: '/login/supplier',
      label: isAr ? 'دخول الموردين' : lang === 'zh' ? '供应商登录' : 'Supplier Login',
    };
    return lang === 'zh' ? [supplier, trader] : [trader, supplier];
  })();

  /* ─── Nav links ───────────────────────────── */
  const isSupplierRegistrationPage = location.pathname.includes('/register/supplier') ||
                                     location.pathname.includes('/login/supplier');
  
  // Signed-in traders and suppliers get the SAME places, with the SAME names,
  // as their dashboard tabs and phone tab bar (lib/navItems.js). About /
  // Contact stay in the footer; the dashboard is a link here, so the separate
  // "لوحتي" button beside the bell is gone (it was the same place twice).
  const signedInNav = !!user && profile?.role === 'buyer' && !isSupplierRegistrationPage;
  // Anyone else signed in (admin, a profile still loading) keeps the old
  // "لوحتي" button: their links are the public ones.
  const showDashboardButton = !!user && !isSupplier && !signedInNav;
  // The cart follows the trader off the store pages (owner, 2026-10-20): from
  // the dashboard they used to have to go back into the store to find it.
  const showCart = signedInNav && hioBuyOnly;
  const supplierLinks = supplierNav()
    // Gated exactly like the dashboard tabs (lib/supplierOnboarding.js):
    // verification gates payout only, so any operational supplier — verified
    // or not — has Requests, Products and Messages. This bar used to hide
    // Requests until `verified` while the dashboard showed it.
    .filter((it) => it.tab !== 'requests' || supplierState?.canAccessRequests)
    .filter((it) => it.tab !== 'my-products' || supplierState?.canAccessProducts)
    .filter((it) => it.tab !== 'messages' || supplierState?.canAccessMessaging)
    .map((it) => ({
      // Rejected / inactive: the first place is that state's page, so it
      // keeps the state's own label ("الحساب").
      label: it.tab === 'overview' && !supplierState?.canAccessRequests ? supplierDashboardLabel : navLabel(it.key, lang),
      path: it.tab === 'overview' ? supplierPrimaryRoute : it.path,
    }));
  const buyerLinks = buyerNav({ hioBuyOnly }).map((it) => ({ label: navLabel(it.key, lang), path: it.path }));

  const links = isSupplier
    ? supplierLinks
    : signedInNav
      ? buyerLinks
      : isSupplierRegistrationPage
        ? [
          { label: isAr ? 'الرئيسية'  : lang === 'zh' ? '首页'  : 'Home',      path: homeRoute },
          { label: isAr ? 'عن مَعبر'  : lang === 'zh' ? '关于'  : 'About',     path: '/about' },
          { label: isAr ? 'تواصل'     : lang === 'zh' ? '联系'  : 'Contact',   path: '/contact' },
        ]
        : [
          { label: isAr ? 'الرئيسية'  : lang === 'zh' ? '首页'  : 'Home',      path: homeRoute },
          // Factories is deliberately absent from the nav. /factories and the
          // factory pages still work and are still linked from product cards,
          // product detail and the buyer dashboard — they are just not offered
          // as a top-level destination.
          { label: isAr ? 'المنتجات'  : lang === 'zh' ? '产品'  : 'Products',  path: productsPath },
          { label: isAr ? 'عن مَعبر'  : lang === 'zh' ? '关于'  : 'About',     path: '/about' },
          { label: isAr ? 'تواصل'     : lang === 'zh' ? '联系'  : 'Contact',   path: '/contact' },
        ];

  const mobileQuickLinks = !isSupplier && !isSupplierRegistrationPage
    ? [
        signedInNav
          ? { label: navLabel('store', lang), path: productsPath }
          : { label: isAr ? 'المنتجات' : lang === 'zh' ? '产品' : 'Products', path: productsPath },
      ]
    : [];

  if (logoOnly) {
    return (
      <nav className={scrolled ? 'scrolled' : ''} style={{ display: 'flex', alignItems: 'center' }}>
        <BrandLogo as="button" size="sm" align="flex-start" onClick={() => nav('/')} />
      </nav>
    );
  }

  return (
    <>
      {/* ══════════════════════════════════════
          NAV BAR
      ══════════════════════════════════════ */}
      <nav className={scrolled ? 'scrolled' : ''}>

        {/* Logo */}
        {/* A signed-in supplier's home is their dashboard; everyone else's is "/". */}
        <BrandLogo as="button" size="sm" align="flex-start" onClick={() => nav(isSupplier && user ? supplierPrimaryRoute : homeRoute)} />

        {/* Desktop links */}
        <ul className="nav-links">
          {links.map((l, i) => (
            <li key={i}>
              <button
                className="nav-link"
                style={isNavLinkActive(l.path)
                  ? { color: 'var(--text-primary)' }
                  : {}}
                onClick={() => nav(l.path)}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Right side */}
        <div className="nav-right">

          {!!mobileQuickLinks.length && (
            <div className="mobile-quick-links">
              {mobileQuickLinks.map((item) => (
                <button
                  key={item.path}
                  className={`nav-mobile-link${isNavLinkActive(item.path) ? ' active' : ''}`}
                  onClick={() => nav(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {/* Lang switcher */}
          <div className="lang-switcher nav-mobile-hide">
            {['ar', 'en', 'zh'].map(l => (
              <button
                key={l}
                className={`lang-btn${lang === l ? ' active' : ''}`}
                onClick={() => setLang(l)}
              >
                {l === 'ar' ? 'AR' : l === 'en' ? 'EN' : '中'}
              </button>
            ))}
          </div>

          {user ? (
            <>
              {showCart && (
                <button className="icon-btn" data-nav-cart="1" onClick={() => nav('/market/cart')}
                  title={isAr ? 'السلة' : lang === 'zh' ? '购物车' : 'Cart'}
                  aria-label={isAr ? 'السلة' : lang === 'zh' ? '购物车' : 'Cart'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <path d="M3 4h2l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.7a1.5 1.5 0 0 0 1.5-1.1L21 8H6.2" />
                    <circle cx="9.5" cy="20" r="1.3" /><circle cx="17.5" cy="20" r="1.3" />
                  </svg>
                  <span className={`icon-badge${cartLines > 0 ? ' show' : ''}`}>{cartLines > 9 ? '9+' : cartLines}</span>
                </button>
              )}
              <NotificationBell user={user} lang={lang} />

              {showDashboardButton && (
                <button className="nav-cta nav-mobile-hide" onClick={() => nav(supplierPrimaryRoute)}>
                  {supplierDashboardLabel}
                </button>
              )}
              <button className="nav-logout nav-mobile-hide" onClick={doSignOut}>
                {isAr ? 'خروج' : lang === 'zh' ? '退出' : 'Logout'}
              </button>
            </>
          ) : (
            <>
              {authDoors.map((d) => (
                <button key={d.key} className="nav-door nav-mobile-hide" onClick={() => nav(d.path)}>
                  <DoorIcon name={d.icon} />
                  <span>{d.label}</span>
                </button>
              ))}
            </>
          )}

          {/* Hamburger */}
          <button
            className={`hamburger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ══════════════════════════════════════
          MOBILE MENU
      ══════════════════════════════════════ */}
      {menuOpen && (
        <div className="mobile-menu open">
          <div className="mobile-menu-lang">
            {['ar', 'en', 'zh'].map((l) => (
              <button
                key={l}
                className={`lang-btn${lang === l ? ' active' : ''}`}
                onClick={() => setLang(l)}
              >
                {l === 'ar' ? 'AR' : l === 'en' ? 'EN' : '中'}
              </button>
            ))}
          </div>

          {links.map((l, i) => (
            <button key={i} className="mobile-menu-item"
              style={{
                ...(isAr ? { fontFamily: 'var(--font-ar)', textAlign: 'right' } : { textAlign: 'left' }),
                ...(isNavLinkActive(l.path) ? { color: 'var(--text-primary)' } : {}),
              }}
              onClick={() => { nav(l.path); setMenuOpen(false); }}>
              {l.label}
            </button>
          ))}

          {/* Signed out, the two doors STACK. `.btn-dark-sm` is white-space:nowrap
              and these labels name their audience, so side by side they overflow
              a phone rather than wrapping. The signed-in pair (dashboard +
              logout) is short and stays in a row. */}
          <div className="mobile-menu-auth" style={user ? undefined : { flexDirection: 'column' }}>
            {user ? (
              <>
                {showDashboardButton && (
                  <button className="btn-dark-sm"
                    onClick={() => { nav(supplierPrimaryRoute); setMenuOpen(false); }}
                    style={{ flex: 1, textAlign: 'center' }}>
                    {supplierDashboardLabel}
                  </button>
                )}
                <button className="btn-outline"
                  onClick={() => { doSignOut(); setMenuOpen(false); }}
                  style={{ flex: 1, textAlign: 'center' }}>
                  {isAr ? 'خروج' : lang === 'zh' ? '退出' : 'Logout'}
                </button>
              </>
            ) : (
              <>
                {authDoors.map((d) => (
                  <button key={d.key} className="nav-door"
                    onClick={() => { nav(d.path); setMenuOpen(false); }}
                    style={{ width: '100%' }}>
                    <DoorIcon name={d.icon} />
                    <span>{d.label}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}